import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Box, Link, Typography } from '@mui/material';

import { getAntibodies } from 'services/resources/commonResources';
import { useUnmountIgnore } from 'utilities/useUnmountIgnore';
import { useSnackbar } from 'utilities/hooks/useSnackbar/useSnackbar';
import { FETCH_ANTIBODIES_ERROR_MESSAGE } from 'constants/errorMessages';
import {
  IHC_ANTIBODIES_BASE_URL,
  IHC_ANTIBODIES_INDEX_URL,
} from 'constants/urls';
import { AntibodyLinkList } from 'components/AntibodiesList/AntibodyLinkList';
import {
  findFacetGroup,
  getFacetUrl,
  isIndexableFacetGroup,
} from 'components/AntibodiesList/antibodyFacets';
import {
  ANTIBODIES_CATALOG_CANONICAL_URL,
  ANTIBODIES_INDEX_DESCRIPTION,
  ANTIBODIES_INDEX_TITLE,
  buildBreadcrumbJsonLd,
  buildCollectionJsonLd,
  getAbsoluteUrl,
  toJsonLdString,
} from 'components/AntibodiesList/antibodySeo';
import 'components/AntibodiesList/styles.css';

const compareByName = (first, second) =>
  first.name.localeCompare(second.name, undefined, { sensitivity: 'base' });

// A facet listing, e.g. "IHC Antibodies Validated for IHC-P". These are hubs that can rank
// for head terms and pass link equity down to the antibody pages; the sitemap lists the
// ones with enough antibodies, and thinner ones are served but marked noindex.
export const AntibodyFacetPage = ({ facet }) => {
  const { facetValue } = useParams();
  const unmountIgnore = useUnmountIgnore();
  const { showError } = useSnackbar();

  const [antibodies, setAntibodies] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    getAntibodies()
      .then((response) => {
        if (unmountIgnore.current) return;

        setAntibodies(response);
        setIsLoaded(true);
      })
      .catch(() => {
        if (unmountIgnore.current) return;

        // Deliberately not a "not found": a fetch that failed says nothing about whether
        // the facet exists, and a crawler must not read noindex out of a transient error.
        setLoadFailed(true);
        showError(FETCH_ANTIBODIES_ERROR_MESSAGE);
      });
  }, [unmountIgnore]);

  const group = useMemo(
    () => (isLoaded ? findFacetGroup(antibodies, facet, facetValue) : null),
    [antibodies, facet, facetValue, isLoaded],
  );

  const listedAntibodies = useMemo(
    () => (group ? [...group.antibodies].sort(compareByName) : []),
    [group],
  );

  const heading = group ? facet.getHeading(group.name) : '';
  // Canonicalise to the group's own slug, so a URL that arrived as an alias or in different
  // case ("cst", "Human") points at the one page instead of competing with it.
  const canonicalUrl = getAbsoluteUrl(
    getFacetUrl(facet, group ? group.slug : facetValue),
  );
  const isIndexable = !!group && isIndexableFacetGroup(group);
  const isMissing = isLoaded && !group;

  const description = group
    ? `${group.antibodies.length} HistoWiz-validated IHC antibodies for ${group.name}. Each links to its catalog page with clone, host species, validated species, supported applications, positive control tissue and reference images.`
    : ANTIBODIES_INDEX_DESCRIPTION;
  const title = group ? `${heading} | HistoWiz` : ANTIBODIES_INDEX_TITLE;

  return (
    <Box component="main" sx={{ maxWidth: 1200, mx: 'auto', px: 3, py: 4 }}>
      {/* defer={false} applies the tags synchronously; helmet's default defers to
          requestAnimationFrame, which never runs in a hidden or non-compositing tab. */}
      <Helmet defer={false}>
        <title>{title}</title>
        <meta name="description" content={description} />
        {!!group && <link rel="canonical" href={canonicalUrl} />}
        {isLoaded && !isIndexable && <meta name="robots" content="noindex" />}
        {!!group && (
          <script type="application/ld+json">
            {toJsonLdString(
              buildCollectionJsonLd({
                title,
                description,
                url: canonicalUrl,
                antibodies: listedAntibodies,
              }),
            )}
          </script>
        )}
        {!!group && (
          <script type="application/ld+json">
            {toJsonLdString(
              buildBreadcrumbJsonLd([
                {
                  name: 'IHC Antibody Catalog',
                  url: ANTIBODIES_CATALOG_CANONICAL_URL,
                },
                {
                  name: 'All Antibodies',
                  url: getAbsoluteUrl(IHC_ANTIBODIES_INDEX_URL),
                },
                { name: heading, url: canonicalUrl },
              ]),
            )}
          </script>
        )}
      </Helmet>

      {!isLoaded && !loadFailed && (
        <Typography variant="body1">Loading antibodies…</Typography>
      )}

      {loadFailed && (
        <>
          <Typography variant="h4" component="h1" gutterBottom>
            Antibodies could not be loaded
          </Typography>
          <Typography variant="body1">
            Please try again shortly, or{' '}
            <Link component={RouterLink} to={IHC_ANTIBODIES_INDEX_URL}>
              browse all IHC antibodies
            </Link>
            .
          </Typography>
        </>
      )}

      {isMissing && (
        <>
          <Typography variant="h4" component="h1" gutterBottom>
            No antibodies found
          </Typography>
          <Typography variant="body1" sx={{ maxWidth: 820 }}>
            Nothing is currently listed by {facet.listLabel.toLowerCase()} here.{' '}
            <Link component={RouterLink} to={IHC_ANTIBODIES_INDEX_URL}>
              Browse all IHC antibodies
            </Link>
            .
          </Typography>
        </>
      )}

      {!!group && (
        <>
          <Typography variant="h4" component="h1" gutterBottom>
            {heading}
          </Typography>
          <Typography variant="body1" sx={{ maxWidth: 820, mb: 3 }}>
            {`${group.antibodies.length} HistoWiz-validated IHC antibodies for ${group.name}, each validated on real tissue.`}{' '}
            <Link component={RouterLink} to={IHC_ANTIBODIES_INDEX_URL}>
              Browse all IHC antibodies
            </Link>{' '}
            or{' '}
            <Link component={RouterLink} to={IHC_ANTIBODIES_BASE_URL}>
              search the catalog
            </Link>
            .
          </Typography>
          <AntibodyLinkList antibodies={listedAntibodies} />
        </>
      )}
    </Box>
  );
};
